import Pusher from "pusher-js";
import {
    LIVE_REMINDER,
    LIVE_ONDEMAND_REMINDER,
    ONDEMAND_REMINDER,
    VPN_REMINDER,
    MEET_THE_EXPERTS_REMINDER,
    LIVE_ATTENDED_EVENT,
    LIVE_NOT_ATTENDED_EVENT,
    LIVE_REMINDER_ONE_WEEK_PRIOR,
    LIVE_REMINDER_ONE_DAY_PRIOR,
    LIVE_REMINDER_ONE_HOUR_PRIOR,
    ONDEMAND_REMINDER_MISSING_TIME,
    SUBSCRIBER_REGISTRATION_CONFIRMED_REMINDER,
    SUBSCRIBER_REGISTRATION_NOT_CONFIRMED_REMINDER,
    NATIONAL_MEETING_US_CA_DUCATI,
    NATIONAL_MEETING_MX_DUCATI
} from "api/config/calendarTypes";
import {
    FILE_VIDEO,
    IMAGES,
    VIDEO,
    FILE_IMAGE,
    PDF,
    FILE_PDF,
} from "config/mime";
import * as channelStatus from "config/channelStatus";

import moment from "moment-timezone";
import {
    REGISTRATION_FLOW_DEFAULT,
    REGISTRATION_FLOW_DUCATI,
    REGISTRATION_FLOW_DUCATI_GDC2024,
    REGISTRATION_FLOW_MSD_CTIO,
    REGISTRATION_FLOW_MSD_VACCINES_ACADEMY,
    REGISTRATION_FLOW_MULTIPLE_STREAM,
    REGISTRATION_FLOW_NOBIVAC_ANIMAL_HEALTH,
    REGISTRATION_FLOW_UNLIMITED_EPISODES,
} from "config/registrationFlows";
import { ATTACHMENT_ICS } from "api/config/attachementTypes";
import { LOGIN } from "api/config/endpoints";
import { getRegistrationForms } from "api/functions/functions";
import { DEFAULT, DIRECT_AUTH_URL } from "config/icsLocationUrlEnums";
import generator from "generate-password"

export const getAuthToken = () => {
    return localStorage.getItem(process.env.REACT_APP_AUTH_TOKEN_NAME);
};

export const setAuthToken = (token) => {
    return localStorage.setItem(process.env.REACT_APP_AUTH_TOKEN_NAME, token);
};

export const getLoginUrl = () => {
    return `/${LOGIN}`;
};

export const createUrl = (chunks) => {
    return chunks.join("/");
};

export const goToLoginPage = () => {
    window.location.replace(getLoginUrl());
};

export const getReminderTitle = (calendarType) => {
    let accordionTitle = "";
    switch (calendarType) {
        case LIVE_REMINDER:
            accordionTitle = "Live event";
            break;
        case LIVE_ONDEMAND_REMINDER:
            accordionTitle = "Live & VOD event";
            break;
        case ONDEMAND_REMINDER:
            accordionTitle = "VOD event";
            break;
        case VPN_REMINDER:
            accordionTitle = "VPN";
            break;
        case MEET_THE_EXPERTS_REMINDER:
            accordionTitle = "Meet the experts";
            break;
        case LIVE_ATTENDED_EVENT:
            accordionTitle = "LIVE event attendees only";
            break;
        case LIVE_NOT_ATTENDED_EVENT:
            accordionTitle = "Users who did not attend the LIVE event";
            break;
        case LIVE_REMINDER_ONE_WEEK_PRIOR:
            accordionTitle = "Live reminder 1 week out (Animal Health)";
            break;
        case LIVE_REMINDER_ONE_DAY_PRIOR:
            accordionTitle = "Live reminder 1 day out (Animal Health)";
            break;
        case LIVE_REMINDER_ONE_HOUR_PRIOR:
            accordionTitle = "Live reminder 1 hour out (Animal Health)";
            break;
        case ONDEMAND_REMINDER_MISSING_TIME:
            accordionTitle = "VOD remaining time (Animal Health)";
            break;
        case SUBSCRIBER_REGISTRATION_CONFIRMED_REMINDER:
            accordionTitle = "Subscribers registration confirmed";
            break;
        case SUBSCRIBER_REGISTRATION_NOT_CONFIRMED_REMINDER:
            accordionTitle = "Subscribers registration NOT confirmed";
            break;
        case NATIONAL_MEETING_US_CA_DUCATI:
            accordionTitle = "National meeting US/CA (Ducati)";
            break;
        case NATIONAL_MEETING_MX_DUCATI:
            accordionTitle = "National meeting MX (Ducati)";
            break;
        default:
    }
    return accordionTitle;
};

/**
 * Returns a Moment object in UTC timezone
 * @returns {import("moment-timezone").Moment}
 * @param {*} dateTime
 */
export const utc = (dateTime) => {
    return moment.tz(dateTime, process.env.REACT_APP_UTC);
};

export const normalizeDate = ({ time, timezone, format }) => {
    if (!format) {
        format = "HH:mm Z";
    }
    let newFormattedTime = utc(time);
    if (timezone) {
        newFormattedTime.tz(timezone);
    }
    return newFormattedTime.format(format);
};

/**
 * Get human readable file size
 * @returns {String}
 * @param {Int} bytes
 */
export const fileSize = (bytes) => {
    bytes = parseFloat(bytes);
    if (!bytes) {
        return null;
    }
    let kB = bytes / 1024;
    if (kB < 1) {
        return `${bytes.toFixed(2)} B`;
    }
    let MB = kB / 1024;
    if (MB < 1) {
        return `${kB.toFixed(2)} kB`;
    }
    let GB = MB / 1024;
    if (GB < 1) {
        return `${MB.toFixed(2)} MB`;
    }
    return `${GB.toFixed(2)} GB`;
};

/**
 * Creates a mime list String from Array
 * @param {Array} mimeList
 * @returns {String}
 */
export const normalizeMime = (mimeList) => {
    if (!mimeList) return "";
    return mimeList.join(", ");
};

export const getFileExtfromMime = (mime) => {
    const chunks = mime.split("/");
    return chunks[1];
};

/**
 * Creates a mime list String from Array
 * @param {Array} mimeList
 * @returns {String}
 */
export const detectFileType = (mime) => {
    if (!mime) {
        return {};
    }

    let fileTypeId = 0;
    let isVideo = false;
    let isImage = false;
    let isPdf = false;

    if (IMAGES.includes(mime)) {
        fileTypeId = FILE_IMAGE;
        isImage = true;
    } else if (VIDEO.includes(mime)) {
        fileTypeId = FILE_VIDEO;
        isVideo = true;
    } else if (PDF.includes(mime)) {
        fileTypeId = FILE_PDF;
        isPdf = true;
    }

    return {
        typeId: fileTypeId,
        ext: getFileExtfromMime(mime),
        isVideo: isVideo,
        isImage: isImage,
        isPdf: isPdf,
    };
};

export const openPreview = ({ event }) => {
    let previewUrl = process.env.REACT_APP_PREVIEW_URL;
    const version = event.domain_association?.aws_amplify_app?.version || event.version

    if (parseInt(version) === 2) {
        previewUrl = previewUrl.replace(/https?:\/\//, 'https://v2.')
    }
    window.open(
        `${previewUrl}?preview=${event.code}`,
        "_blank"
    );
};

export const consoleLog = (...string) => {
    if (process.env.NODE_ENV === "development") {
        console.log(string);
    }
};

export const initPusher = () => {
    return new Pusher(process.env.REACT_APP_PUSHER_APP_KEY, {
        // key: process.env.REACT_APP_PUSHER_APP_KEY,
        cluster: process.env.REACT_APP_PUSHER_APP_CLUSTER,
        forceTLS: true,
        authEndpoint:
            process.env.REACT_APP_API_BASEURL + "private/broadcasting/auth",
        pongTimeout: 6000,
        activityTimeout: 30000,
        auth: {
            headers: {
                Authorization: "Bearer " + getAuthToken(),
            },
        },
    });
};

export const translateChannelStatus = (statusCode) => {
    statusCode = parseInt(statusCode);
    switch (statusCode) {
        // // UNKNOWN
        case channelStatus.UNKNOWN:
            return "UNKNOWN";
        case channelStatus.PRE_LIVE:
            return "PRE_LIVE";
        case channelStatus.LIVE:
            return "LIVE";
        case channelStatus.BUFFER:
            return "BUFFER";
        case channelStatus.BYE:
            return "BYE";
        case channelStatus.PRE_VOD:
            return "PRE_VOD";
        case channelStatus.VOD:
            return "VOD";
        case channelStatus.POST_VOD:
            return "POST_VOD";
        default:
            return "";

        // // LIVE ONLY
        // const PRE_LIVE = 101;
        // const LIVE = 121;

        // // LIVE & VOD
        // const BUFFER = 202;
        // const BYE = 262;

        // // VOD ONLY
        // const PRE_VOD = 303;
        // const VOD = 323;
        // const POST_VOD = 343;
    }
};

export const str2bytes = (str) => {
    var bytes = new Uint8Array(str.length);
    for (var i = 0; i < str.length; i++) {
        bytes[i] = str.charCodeAt(i);
    }
    return bytes;
};

export const registrationFormsOptions = async () => {
    const dataRf = await getRegistrationForms();
    return dataRf?.map((item) => {
        return {
            id: `${item.id}`,
            label: item.title,
        };
    });
    // return [{
    //     id: "1",
    //     label: "#1, AP CMV (Cristina)"
    // }, {
    //     id: "2",
    //     label: "#2, APID (Michela)"
    // }, {
    //     id: "3",
    //     label: "#3, MSD_HPV_EUROGIN_2021 (Eleonaora)"
    // }, {
    //     id: "4",
    //     label: "#4, MSD HPV HN Cancer (Cristina)"
    // }, {
    //     id: "5",
    //     label: "#5, MSD ID FORUM (Michela)"
    // }, {
    //     id: "6",
    //     label: "#6, MSD VACCINES ACADEMY (Cristina)"
    // }, {
    //     id: "7",
    //     label: "#7, MSD AP NMB (Jennifer)"
    // }, {
    //     id: "8",
    //     label: "#8, MSD JCAP (Eleonaora)"
    // }, {
    //     id: "9",
    //     label: "#9, MSD AP SMART MEETING (Ilaria)"
    // }, {
    //     id: "10",
    //     label: "#10, MSD COVID SASS (Michela)"
    // }, {
    //     id: "11",
    //     label: "#11, MSD GLOBAL HPV (Cristina)"
    // }, {
    //     id: "12",
    //     label: "#12, MSD AP NIAS (Eleonora)"
    // }, {
    //     id: "13",
    //     label: "#13, MSD JCAP MATCH (Eleonora)"
    // }, {
    //     id: "14",
    //     label: "#14, MSD AP NIAS (B) (Greta)"
    // }, {
    //     id: "15",
    //     label: "#15, AP ID Forum - COVID-19 (Greta)"
    // }, {
    //     id: "16",
    //     label: "#16, AP ID Forum - GN Webinar (Greta)"
    // }, {
    //     id: "17",
    //     label: "#17, MSD Vaccines Academy 2022 (Michela)"
    // }, {
    //     id: "18",
    //     label: "#18, MSD AP ID Forum - COVID-19 (2) (Greta)"
    // }, {
    //     id: "19",
    //     label: "#19, MSD Global CMV (Michela)"
    // }, {
    //     id: "20",
    //     label: "#20, Organon Virtual SASS (Cristina)"
    // }, {
    //     id: "21",
    //     label: "#21, MSD AP H&N (Michela)"
    // }, {
    //     id: "1001",
    //     label: "#1001, MSD CTIO 2022 (Eleonora)"
    // }]
};

export const registrationFlows = () => {
    return [
        {
            id: REGISTRATION_FLOW_DEFAULT,
            label: "Default",
        },
        {
            id: REGISTRATION_FLOW_MSD_CTIO,
            label: "#1, MSD CTIO (A, B, C registration modes)",
        },
        {
            id: REGISTRATION_FLOW_MSD_VACCINES_ACADEMY,
            label: "#2, MSD VACCINES ACADEMY",
        },
        {
            id: REGISTRATION_FLOW_NOBIVAC_ANIMAL_HEALTH,
            label: "#3, NOBIVAC ANIMAL HEALTH",
        },
        {
            id: REGISTRATION_FLOW_MULTIPLE_STREAM,
            label: "#4, MULTIPLE STREAM",
        },
        {
            id: REGISTRATION_FLOW_DUCATI,
            label: "#5, V2 DEFAULT FLOW",
        },
        {
            id: REGISTRATION_FLOW_UNLIMITED_EPISODES,
            label: "#6, UNLIMITED EPISODES",
        },
        {
            id: REGISTRATION_FLOW_DUCATI_GDC2024,
            label: "#7, DUCATI GDC 2024",
        },
    ];
};

export const icsLocationUrl = () => {
    return [
        {
            id: DEFAULT,
            label: "Event domain",
        },
        {
            id: DIRECT_AUTH_URL,
            label: "#1, Direct login URL",
        },
    ];
};

// export const getRegistrationFormName = (formId) => {
//     let res = ""
//     const result = registrationForms().find(item => parseInt(item.id) === parseInt(formId))
//     try {
//         res = result.label
//         // console.log("getRegistrationFormName", formId, registrationForms())
//     }
//     catch (e) { }
//     return res
// }

export const getCellIdName = (cellId) => {
    // const chunk = cellId.split(":")
    const re = new RegExp(/(?<=.:).+/);
    const match = cellId.match(re);
    return match[0];
};

export const getTableCell = (row, cellName) => {
    return row.cells.find((item) => item.id === `${row.id}:${cellName}`);
};

export const getTableCellValue = (row, cellName) => {
    return getTableCell(row, cellName).value;
};

export const getLocalTz = () => {
    return moment.tz.guess(true);
};

export const getIcsAttachemntParams = () => {
    return {
        calendar_type: MEET_THE_EXPERTS_REMINDER,
        attachment_type: ATTACHMENT_ICS,
    };
};

export const getChannelStatusList = () => {
    return [
        {
            id: channelStatus.PRE_LIVE,
            label: "Pre-LIVE (for channels having Live stream)",
        },
        {
            id: channelStatus.LIVE,
            label: "LIVE (for channels having Live stream)",
        },
        {
            id: channelStatus.PRE_VOD,
            label: "Pre-VOD (for channels having only VOD, NO Live stream!)",
        },
        {
            id: channelStatus.VOD,
            label: "VOD (for channels having VOD)",
        },
        {
            id: channelStatus.POST_VOD,
            label: "Post-VOD (for channels having VOD)",
        },
        {
            id: channelStatus.BUFFER,
            label: "Buffer (for channels having both Live stream and VOD)",
        },
        {
            id: channelStatus.BYE,
            label: "BYE",
        },
    ];
};

export const parseHttpErrors = (e) => {
    return e?.response?.data?.errors;
};

export const formatAceEditorContent = (value) => {
    return JSON.stringify(value, null, "\t");
};

export const getDateFormats = () => {
    return [
        {
            id: "d F Y",
            label: "Day Month Year (09 June 2021)",
        },
        {
            id: "j F Y",
            label: "Day Month Year (9 June 2021)",
        },
        {
            id: "F j, Y",
            label: "Month Day, Year (June 9, 2021)",
        },
        {
            id: "F d Y",
            label: "Month Day Year (June 09 2021)",
        },
        {
            id: "F dS Y",
            label: "Month Day+Suffix Year (June 9th 2021)",
        },
        {
            id: "M jS, Y",
            label: "Month (a short textual representation) Day+Suffix, Year (Jun 9th, 2021)",
        },
        {
            id: "j-n-Y",
            label: "Day-Month-Year (9-6-2021)",
        },
        {
            id: "n-j-Y",
            label: "Month-Day-Year (6-9-2021)",
        },
        {
            id: "j/n/Y",
            label: "Day/Month/Year (9/6/2021)",
        },
        {
            id: "n/j/Y",
            label: "Month/Day/Year (6/9/2021)",
        },
    ];
};

export const getLogoHeightOptions = () => {
    return [
        {
            id: "1rem",
            label: "1rem",
        },
        {
            id: "1.5rem",
            label: "1.5rem",
        },
        {
            id: "2rem",
            label: "2rem",
        },
        {
            id: "2.3rem",
            label: "2.3rem",
        },
        {
            id: "2.5rem",
            label: "2.5rem",
        },
        {
            id: "2.7rem",
            label: "2.7rem",
        },
        {
            id: "3rem",
            label: "3rem",
        },
        {
            id: "3.5rem",
            label: "3.5rem"
        }
    ]
}

export const getWatermarkOpacity = () => {
    let out = []
    for (let i = 1; i <= 10; i++) {
        out.push({
            id: i === 10 ? `${i}` : `0.${i}`,
            label: `${i}0%`
        })
    }
    return out
}

export const checkIfObjectHasValues = (obj) => {
    if (!obj) return false

    for (let key in obj) {
        if (obj[key]) {
            return true;
        }
    }
    return false
}
export const generatePassword = (options) => {
    return generator.generate({
        length: options.minLength,
        uppercase: options.includeMixedCase,
        lowercase: options.includeLetters,
        numbers: options.includeNumbers,
        symbols: options.includeSymbols,
        excludeSimilarCharacters: true,
        strict: options.strict
    });
}

export const getFormatAwsAppsOptions = (awsApps = []) => {
    return awsApps?.map(item => {
        return {
            id: String(item.id),
            label: `${item.region_name} v${item.version} (${item.region_id} / ${item.description})`
        }
    });
}

export const getFormatAwsAppsOptionsByThemeVersion = () => {

    return [
        {
            id: process.env.REACT_APP_DEFAULT_V1_AMPLIFY_APP_ID,
            label: `Theme V1`
        },
        {
            id: process.env.REACT_APP_DEFAULT_V2_AMPLIFY_APP_ID,
            label: `Theme V2`
        },
    ]
}

export const getAppIdFormField = ({ awsApps = [], isAdmin = false, getValue, fieldName = "app_id" }) => {
    if (isAdmin) {
        return {
            elementType: "select",
            //   helperText: "Timezone",
            id: fieldName,
            name: fieldName,
            label: "AWS App",
            //   invalidText: "This must be a valid email address",
            labelText: "Registration form (Leave blank to use default)",
            value: getValue(fieldName),
            items: getFormatAwsAppsOptions(awsApps),
        }
    }
    return {
        elementType: "select",
        //   helperText: "Timezone",
        id: fieldName,
        name: fieldName,
        label: "Theme version",
        //   invalidText: "This must be a valid email address",
        value: getValue(fieldName) || process.env.REACT_APP_DEFAULT_V1_AMPLIFY_APP_ID,
        items: getFormatAwsAppsOptionsByThemeVersion(),
    }
}

export const isOnlyNumbers = (value) => {
    if (!value) return false
    return /^\d+$/.test(value)
};

export const subscribeToChannel = (eventId, episodeId) => {
    return window.Pusher.subscribe(`private-${eventId}.${episodeId}`)
}
export const unsubscribeFromChannel = (channel) => {
    window.Pusher.unsubscribe(channel.name)
}
export const eventIsCleaned = (event) => {
    return event?.clean_subscriptions_logs?.length > 0;
}

export const _getEpisodeAvailableCountries = (channels) => {
    let c = [];
    if (channels?.length) {
        channels.forEach(element => {
            c = c.concat(element.countries)
        });
    }
    return c;
}

export const _availableCountriesIncludes = ({ availableCountries, countryId }) => {
    return availableCountries?.includes(countryId);
}
